import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, GraduationCap, Clock, BookOpen, CheckCircle2, Award, Sparkles,
  Lock, Play, Loader2, X, AlertCircle, Mail, User2, Phone,
} from "lucide-react";
import CertificateMockup from "../components/CertificateMockup";
import { loginWithGoogle as startGoogleLogin } from "../lib/userAuth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";
const ACCENT_DEEP = "#0891B2";

function getAuthConfig() {
  const tok = localStorage.getItem("ai_tools_token");
  return tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
}

export default function CourseDetailPage() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showSignup, setShowSignup] = useState(false);
  // Existing-enrollment detection: if the logged-in user already owns this course
  // we swap "Enroll Now → ₹X" with "Continue Course → / View Certificate" so they
  // never accidentally pay twice.
  const [myEnrollment, setMyEnrollment] = useState(null);

  useEffect(() => {
    axios.get(`${API}/courses/${slug}`).then((r) => setCourse(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Course not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  // Detect existing enrollment when the user is signed in.
  useEffect(() => {
    const stored = sessionStorage.getItem("user_data");
    if (!stored) return;
    let token;
    try { token = JSON.parse(stored)?.session_token; } catch (_) { return; }
    if (!token) return;
    axios
      .get(`${API}/my/course-enrollments`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const match = (r.data?.items || []).find((it) => it.slug === slug);
        if (match) setMyEnrollment(match);
      })
      .catch(() => {});
  }, [slug]);

  // Auto-open signup modal after Google auth redirect back here (intent saved in sessionStorage)
  useEffect(() => {
    const intent = sessionStorage.getItem("shansolearn_purchase_intent");
    if (intent === slug || window.location.hash === "#purchase") {
      setShowSignup(true);
      sessionStorage.removeItem("shansolearn_purchase_intent");
      if (window.location.hash) {
        try { window.history.replaceState(null, "", window.location.pathname); } catch (_) { /* ignore */ }
      }
    }
  }, [slug]);

  if (loading) return <CenterBox>Loading…</CenterBox>;
  if (err) return <CenterBox>{err}</CenterBox>;
  if (!course) return null;

  const previewModule = course.modules?.[0];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/courses" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm" data-testid="back-to-catalog">
            <ArrowLeft size={16} /> All courses
          </Link>
          <div className="flex items-center gap-2" style={{ color: ACCENT }}>
            <GraduationCap size={18} />
            <span className="font-semibold tracking-tight text-sm">ShansoLearn</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-3">
              <Sparkles size={12} /> Self-paced video course
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]">{course.title}</h1>
            {course.summary && <p className="text-zinc-400 text-base mt-4 leading-relaxed">{course.summary}</p>}
            <div className="flex items-center flex-wrap gap-3 text-[12px] text-zinc-500 mt-5">
              <span className="inline-flex items-center gap-1.5"><BookOpen size={13} /> {course.modules_count} modules</span>
              {course.duration_min_total && <span className="inline-flex items-center gap-1.5"><Clock size={13} /> ~{Math.round(course.duration_min_total / 60)} hours</span>}
              <span className="inline-flex items-center gap-1.5"><Award size={13} /> Verified certificate</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={13} /> Lifetime access</span>
            </div>
          </div>

          {previewModule?.video_url_preview && (
            <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl overflow-hidden" data-testid="preview-player">
              <div className="p-3 flex items-center justify-between border-b border-zinc-800/60">
                <p className="text-white text-sm font-semibold">▶ Preview: Module 1 — {previewModule.title}</p>
                <span className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">FREE PREVIEW</span>
              </div>
              <video src={previewModule.video_url_preview} controls className="w-full bg-black aspect-video" preload="metadata" />
            </div>
          )}

          {/* ── What you'll earn — tilted certificate mockup ─────────────── */}
          <div data-testid="what-youll-earn">
            <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Award size={18} className="text-cyan-300" /> What you'll earn
              </h2>
              <p className="text-zinc-500 text-xs">Verified · Shareable · Printed with your name</p>
            </div>
            <CertificateMockup course={course} />
          </div>

          <div>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><BookOpen size={18} className="text-cyan-300" /> Curriculum</h2>
            <div className="space-y-2" data-testid="curriculum-list">
              {course.modules.map((m, i) => (
                <div
                  key={i}
                  className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 flex items-start gap-3"
                  data-testid={`curriculum-item-${i}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${i === 0 ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "bg-zinc-800 text-zinc-500"}`}>
                    {i === 0 ? <Play size={13} /> : <Lock size={13} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-white text-[15px] font-semibold leading-tight">M{m.module_no}: {m.title}</p>
                      {m.duration_min && <span className="text-zinc-500 text-[11px]">~{m.duration_min} min</span>}
                    </div>
                    {m.summary && <p className="text-zinc-400 text-[13px] mt-1 leading-relaxed">{m.summary}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {(course.prerequisites?.length > 0 || course.target_audience) && (
            <div className="grid sm:grid-cols-2 gap-4">
              {course.target_audience && (
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
                  <p className="text-white text-sm font-bold mb-2">Who this is for</p>
                  <p className="text-zinc-400 text-sm leading-relaxed">{course.target_audience}</p>
                </div>
              )}
              {course.prerequisites?.length > 0 && (
                <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
                  <p className="text-white text-sm font-bold mb-2">Prerequisites</p>
                  <ul className="text-zinc-400 text-sm space-y-1 list-disc list-inside">
                    {course.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 bg-zinc-900/60 border border-cyan-500/30 rounded-2xl p-6 space-y-4" data-testid="buy-box">
            {myEnrollment ? (
              // Already enrolled — show Continue / View Certificate instead of Enroll
              <EnrolledBuyBox enrollment={myEnrollment} course={course} />
            ) : (
              // Not enrolled — original Enroll Now flow
              <>
                <div>
                  <span className="text-cyan-300 font-bold text-3xl">₹{course.price_inr}</span>
                  {course.original_price_inr && course.original_price_inr > course.price_inr && (
                    <span className="text-zinc-500 text-base line-through ml-2">₹{course.original_price_inr}</span>
                  )}
                  <p className="text-zinc-400 text-xs mt-1">One-time payment · Lifetime access</p>
                </div>
                <button
                  onClick={() => setShowSignup(true)}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg text-black font-bold text-base hover:opacity-90 transition-opacity"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                  data-testid="enroll-btn"
                >
                  Enroll Now →
                </button>
                <ul className="text-[13px] text-zinc-300 space-y-2 pt-2">
                  <li className="inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> {course.modules_count} self-paced video modules</li>
                  <li className="inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> Quizzes after every module</li>
                  <li className="inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> Final assessment (30 questions, 80% to pass)</li>
                  <li className="inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> Verified certificate from shansoai.com</li>
                  <li className="inline-flex items-start gap-2"><CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" /> No subscription · No recurring fees</li>
                </ul>
              </>
            )}
          </div>
        </div>
      </main>

      {showSignup && <SignupModal course={course} onClose={() => setShowSignup(false)} />}
    </div>
  );
}

// ─── Buy box variant shown when the logged-in user already owns this course ──
function EnrolledBuyBox({ enrollment, course }) {
  const completed = !!enrollment.certificate_id;
  const learnHref = `/learn/${enrollment.slug}?token=${enrollment.token}`;
  return (
    <>
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border w-fit"
        style={{
          background: completed ? "rgba(16,185,129,0.12)" : "rgba(34,211,238,0.10)",
          borderColor: completed ? "rgba(16,185,129,0.45)" : "rgba(34,211,238,0.40)",
          color: completed ? "#34d399" : ACCENT,
        }}
        data-testid="enrolled-badge"
      >
        <CheckCircle2 size={12} />
        {completed ? "Course completed" : "You\u2019re enrolled"}
      </div>
      <p className="text-white text-base font-bold leading-snug">
        {completed
          ? "Nice work — your certificate is waiting."
          : "Welcome back! Pick up where you left off."}
      </p>
      <p className="text-zinc-400 text-xs">
        {completed
          ? "Re-watch any module any time, or grab your shareable certificate below."
          : "Your progress is saved across devices. No need to enrol again."}
      </p>
      <Link
        to={learnHref}
        className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg text-black font-bold text-base hover:opacity-90 transition-opacity"
        style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
        data-testid="continue-course-btn"
      >
        <Play size={16} /> {completed ? "Re-watch course" : "Continue course"} →
      </Link>
      {completed && (
        <Link
          to={`${learnHref}#certificate`}
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 font-bold text-sm hover:bg-emerald-500/20"
          data-testid="view-cert-btn"
        >
          <Award size={14} /> View certificate
        </Link>
      )}
      <Link
        to="/my-courses"
        className="block text-center text-cyan-300 text-xs hover:text-cyan-200 underline-offset-2 hover:underline"
        data-testid="see-all-enrolled"
      >
        See all your courses →
      </Link>
      <ul className="text-[12px] text-zinc-400 space-y-1.5 pt-2 border-t border-zinc-800">
        <li className="inline-flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5 shrink-0" /> {course.modules_count} modules · lifetime access</li>
        <li className="inline-flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5 shrink-0" /> Progress &amp; quizzes synced across devices</li>
        {completed && (
          <li className="inline-flex items-start gap-2"><CheckCircle2 size={12} className="text-emerald-400 mt-0.5 shrink-0" /> Certificate verifiable on shansoai.com</li>
        )}
      </ul>
    </>
  );
}

function CenterBox({ children }) {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-400 flex items-center justify-center">{children}</div>
  );
}

function SignupModal({ course, onClose }) {
  const [mode, setMode] = useState("google"); // google | email
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [user, setUser] = useState(null);
  const submitOnceRef = useRef(false);

  // Detect existing Google session on mount
  useEffect(() => {
    const stored = sessionStorage.getItem("user_data");
    if (stored) {
      try {
        const u = JSON.parse(stored);
        if (u?.email) {
          setUser(u);
          setEmail(u.email);
          setName(u.name || "");
        }
      } catch (_) { /* ignore */ }
    }
  }, []);

  function loginWithGoogle() {
    // Save intent so we re-open this modal after auth redirect strips the URL hash
    try { sessionStorage.setItem("shansolearn_purchase_intent", course.slug); } catch (_) { /* ignore */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin(`/courses/${course.slug}`);
  }

  async function startCheckout() {
    if (submitOnceRef.current) return;
    setErr("");
    const finalName = name.trim();
    const finalEmail = (user?.email || email).trim().toLowerCase();
    const phoneDigits = phone.replace(/\D/g, "");
    if (finalName.length < 2) return setErr("Please enter your full name (this will appear on your certificate).");
    if (!finalEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) return setErr("Enter a valid email.");
    if (phoneDigits.length < 10) return setErr("Please enter a valid 10-digit mobile number.");

    submitOnceRef.current = true;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/courses/${course.slug}/signup`,
        { full_name: finalName, email: finalEmail, phone: phoneDigits.slice(-10), origin_url: window.location.origin },
        getAuthConfig(),
      );
      if (res.data.already_enrolled) {
        if (res.data.magic_token) {
          window.location.href = `/learn/${course.slug}?token=${res.data.magic_token}`;
          return;
        }
        setErr(res.data.message);
        submitOnceRef.current = false;
        setSubmitting(false);
        return;
      }
      if (!window.Cashfree) {
        const script = document.createElement("script");
        script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        document.head.appendChild(script);
        await new Promise((r) => { script.onload = r; });
      }
      const cf = window.Cashfree({ mode: "production" });
      cf.checkout({ paymentSessionId: res.data.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Payment initiation failed. Please try again.");
      submitOnceRef.current = false;
      setSubmitting(false);
    }
  }

  const useGoogleVerified = !!user;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="signup-modal">
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-zinc-900">
          <p className="text-white font-bold text-base">Enroll · {course.title}</p>
          <button onClick={onClose} className="text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-900" data-testid="close-signup"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {useGoogleVerified ? (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-3" data-testid="google-verified-banner">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-300 font-bold">{(user.name || user.email)[0]?.toUpperCase()}</div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-bold truncate">Signed in</p>
                <p className="text-emerald-300 text-xs truncate">{user.email} · Verified via Google</p>
              </div>
            </div>
          ) : mode === "google" ? (
            <>
              <button
                onClick={loginWithGoogle}
                className="w-full inline-flex items-center justify-center gap-2.5 px-4 py-3 rounded-lg bg-white hover:bg-zinc-100 text-zinc-900 font-bold text-sm transition-colors"
                data-testid="google-login-btn"
              >
                <GoogleIcon /> Sign in with Google to enroll
              </button>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-zinc-800" />
                <span className="text-zinc-500 text-[10px] uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-zinc-800" />
              </div>
              <button
                onClick={() => setMode("email")}
                className="w-full text-cyan-300 hover:text-cyan-200 text-sm font-semibold"
                data-testid="use-email-instead-btn"
              >
                Sign up with email →
              </button>
            </>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-zinc-400 text-xs">Enter your details to enroll.</p>
              <button onClick={() => setMode("google")} className="text-cyan-300 hover:text-cyan-200 text-xs font-semibold" data-testid="back-to-google-btn">
                ← Use Google
              </button>
            </div>
          )}

          {/* Full Name + Mobile are ALWAYS required (printed on certificate + payment) */}
          {(useGoogleVerified || mode === "email") && (
            <>
              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"><User2 size={11} /> Full name <span className="text-rose-400">*</span></label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="As it should appear on your certificate"
                  className="w-full bg-zinc-900 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none"
                  data-testid="signup-name"
                  maxLength={80}
                />
                <p className="text-zinc-500 text-[10px] mt-1">This name will be printed on your certificate.</p>
              </div>

              {!useGoogleVerified && (
                <div>
                  <label className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"><Mail size={11} /> Email <span className="text-rose-400">*</span></label>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    type="email"
                    className="w-full bg-zinc-900 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none"
                    data-testid="signup-email"
                  />
                  <p className="text-zinc-500 text-[10px] mt-1">We'll email your course access link + certificate here.</p>
                </div>
              )}

              <div>
                <label className="text-zinc-400 text-xs uppercase tracking-wider font-bold mb-1.5 flex items-center gap-1"><Phone size={11} /> Mobile number <span className="text-rose-400">*</span></label>
                <div className="flex items-stretch gap-2">
                  <div className="bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-zinc-400 text-sm shrink-0 select-none">+91</div>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                    placeholder="10-digit mobile"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    className="flex-1 bg-zinc-900 border border-zinc-800 focus:border-cyan-500/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none tracking-wider"
                    data-testid="signup-phone"
                  />
                </div>
                <p className="text-zinc-500 text-[10px] mt-1">Required by the payment gateway for receipts and order updates.</p>
              </div>
            </>
          )}

          {err && (
            <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs p-2.5 rounded-lg inline-flex items-start gap-2 w-full" data-testid="signup-error">
              <AlertCircle size={14} className="shrink-0 mt-0.5" /> {err}
            </div>
          )}

          {(useGoogleVerified || mode === "email") && (
            <button
              onClick={startCheckout}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg text-black font-bold text-base disabled:opacity-60 transition-opacity"
              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
              data-testid="checkout-btn"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</> : `Pay ₹${course.price_inr} & Enroll →`}
            </button>
          )}

          <p className="text-zinc-500 text-[10px] text-center">Secure payment via Cashfree · UPI · Cards · Net Banking</p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 01-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.94v2.32A9 9 0 009 18z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.41 5.41 0 013.68 9c0-.6.1-1.18.29-1.72V4.96H.94A9 9 0 000 9c0 1.45.35 2.83.94 4.04l3.03-2.32z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 009 0 9 9 0 00.94 4.96l3.03 2.32C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}
